import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import MapboxMap from "@/components/MapboxMap";
import { MapPin, Fish, X, Search, Loader2, PanelLeftClose, PanelLeft } from "lucide-react";
import { Input } from "@/components/ui/input";
import NavMenu from "@/components/NavMenu";
import { useGetApp } from "@/context/GetAppContext";
import { useDebounce } from "@/hooks/useDebounce";
import { useDesktopSearch } from "@/hooks/useDesktopSearch";
import { useDestinationsData } from "@/hooks/useDestinationsData";
import { SearchResultsDropdown } from "@/components/destinations/SearchResultsDropdown";
import { DirectionsDialog } from "@/components/destinations/DirectionsDialog";
import { MapFilterBar } from "@/components/destinations/MapFilterBar";
import { MobileFilterSheet } from "@/components/destinations/MobileFilterSheet";
import { DestinationCard } from "@/components/destinations/DestinationCard";
import { MobileDestinationsLayout } from "@/components/destinations/MobileDestinationsLayout";
import { DestinationsDialogs } from "@/components/destinations/DestinationsDialogs";

export default function Destinations() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const openGetApp = useGetApp();
  const [destinations, setDestinations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedState, setSelectedState] = useState("all");
  const [selectedDestination, setSelectedDestination] = useState(null);
  const [mapCenter, setMapCenter] = useState(null);
  const [directionsOrigin, setDirectionsOrigin] = useState("");
  const [directionsDialogOpen, setDirectionsDialogOpen] = useState(false);
  const [directionsDestination, setDirectionsDestination] = useState(null);
  const [weatherDialogOpen, setWeatherDialogOpen] = useState(false);
  const [selectedAccessPoint, setSelectedAccessPoint] = useState(null);
  const [searchZoom, setSearchZoom] = useState(null);
  const [urlParamsProcessed, setUrlParamsProcessed] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const [mobileSearchQuery, setMobileSearchQuery] = useState(""), [mobileSearchResults, setMobileSearchResults] = useState(null);
  const [mobileSearchLoading, setMobileSearchLoading] = useState(false), [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [mobileViewMode, setMobileViewMode] = useState('map');
  
  // Desktop search state
  const boundsTimeoutRef = useRef(null);
  
  const [viewportBounds, setViewportBounds] = useState(null);
  const [isPanelCollapsed, setIsPanelCollapsed] = useState(false);
  const currentMapViewRef = useRef(null);
  
  const {
    desktopSearchQuery, setDesktopSearchQuery,
    desktopSearchResults, desktopSearchLoading,
    desktopSearchOpen, setDesktopSearchOpen,
    desktopSelectedIndex, desktopSearchRef, desktopDropdownRef,
    fetchDesktopSearchResults, handleDesktopSelectLocation,
    handleDesktopKeyDown, clearDesktopSearch,
  } = useDesktopSearch({ destinations, setMapCenter, setSearchZoom, setSelectedDestination });
  
  const debouncedDesktopQuery = useDebounce(desktopSearchQuery, 300);
  
  // Layer toggles
  const [showRivers, setShowRivers] = useState(true);
  const [showLakes, setShowLakes] = useState(true);
  const [showPOIs, setShowPOIs] = useState(true);
  const [selectedPOITypes, setSelectedPOITypes] = useState([
    'fishing_access', 'boat_ramp', 'stream_gauge', 'rapids', 'dam',
    'waterfall', 'bridge', 'hazard', 'fly_shop', 'lodging', 'campsite',
    'trailhead', 'parking', 'restroom', 'other'
  ]);
  const [poiTypeConfig, setPOITypeConfig] = useState({ types: [], categories: {} });

  const saveInApp = useCallback((e) => { e?.stopPropagation?.(); openGetApp("Saving favorites"); }, [openGetApp]);

  const {
    fetchDestinations, fetchPOITypes,
    openDirectionsDialog, getDirections,
    handleSearchDestination, handleSearchAccessPoint, handleSearchLocation,
  } = useDestinationsData({
    destinations, setDestinations, setLoading,
    setPOITypeConfig,
    setMapCenter, setSearchZoom, setSelectedDestination, setSelectedAccessPoint,
    directionsDestination, setDirectionsDialogOpen, directionsOrigin, setDirectionsOrigin,
    setDirectionsDestination,
  });
  
  // ---- Map state persistence ----
  const saveMapState = useCallback(() => {
    if (currentMapViewRef.current) {
      const mapState = {
        ...currentMapViewRef.current,
        selectedState, showRivers, showLakes, showPOIs, selectedPOITypes,
        timestamp: Date.now()
      };
      sessionStorage.setItem('destinationsMapState', JSON.stringify(mapState));
    }
  }, [selectedState, showRivers, showLakes, showPOIs, selectedPOITypes]);
  
  const handleDestinationNavigate = useCallback((dest) => {
    saveMapState();
    navigate(`/destinations/${dest.slug || dest.id}`);
  }, [saveMapState, navigate]);
  
  // Restore map state on mount
  useEffect(() => {
    try {
      const savedState = sessionStorage.getItem('destinationsMapState');
      if (savedState) {
        const parsed = JSON.parse(savedState);
        if (parsed.timestamp && Date.now() - parsed.timestamp < 30 * 60 * 1000) {
          if (parsed.center && parsed.zoom) { setMapCenter([parsed.center.lat, parsed.center.lng]); setSearchZoom(parsed.zoom); }
          if (parsed.selectedState) setSelectedState(parsed.selectedState);
          if (typeof parsed.showRivers === 'boolean') setShowRivers(parsed.showRivers);
          if (typeof parsed.showLakes === 'boolean') setShowLakes(parsed.showLakes);
          if (typeof parsed.showPOIs === 'boolean') setShowPOIs(parsed.showPOIs);
          if (parsed.selectedPOITypes) setSelectedPOITypes(parsed.selectedPOITypes);
          setUrlParamsProcessed(true);
        }
        sessionStorage.removeItem('destinationsMapState');
      }
    } catch (e) {}
  }, []);
  
  // Responsive
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    return () => { if (boundsTimeoutRef.current) clearTimeout(boundsTimeoutRef.current); };
  }, []);

  // URL params from landing page search
  useEffect(() => {
    if (urlParamsProcessed) return;
    const lat = searchParams.get('lat');
    const lng = searchParams.get('lng');
    const zoom = searchParams.get('zoom');
    const stateParam = searchParams.get('state');
    const destId = searchParams.get('destId');
    const apId = searchParams.get('apId');
    let hasParams = false;
    
    if (lat && lng) {
      const latitude = parseFloat(lat);
      const longitude = parseFloat(lng);
      if (!isNaN(latitude) && !isNaN(longitude)) {
        setMapCenter([latitude, longitude]);
        setSearchZoom(zoom ? parseInt(zoom) : 10);
        hasParams = true;
      }
    }
    if (stateParam) { setSelectedState(stateParam); hasParams = true; }
    if (destId || apId) hasParams = true;
    if (hasParams) setUrlParamsProcessed(true);
  }, [searchParams, urlParamsProcessed]);

  // Deep-link to destination or access point
  useEffect(() => {
    const destId = searchParams.get('destId');
    const apId = searchParams.get('apId');
    
    if (destId && destinations.length > 0) {
      const dest = destinations.find(d => d.id === destId);
      if (dest) {
        setSelectedDestination(dest);
        setMapCenter([dest.latitude, dest.longitude]);
        setSearchZoom(12);
        setSearchParams({}, { replace: true });
      }
    }
    if (apId) {
      axios.get(`${API}/access-points/${apId}`)
        .then(response => {
          const ap = response.data;
          if (ap) {
            setSelectedAccessPoint(ap);
            setMapCenter([ap.latitude, ap.longitude]);
            setSearchZoom(14);
            setSearchParams({}, { replace: true });
          }
        })
        .catch(() => {});
    }
  }, [destinations, searchParams, setSearchParams]);

  // ---- Search handlers ----
  const handleSearchSelect = useCallback((location) => {
    setMapCenter([location.latitude, location.longitude]);
    setSearchZoom(location.type === "location" ? 10 : 12);
    if (location.type === "destination") {
      const dest = destinations.find(d => d.id === location.data.id);
      if (dest) setSelectedDestination(dest);
    } else if (location.type === "access_point") {
      setSelectedAccessPoint(location.data);
    }
    // Clear after fly animation
    setTimeout(() => { setMapCenter(null); setSearchZoom(null); }, 2000);
  }, [destinations]);

  const handleDestinationSelect = useCallback((dest) => {
    setSelectedDestination(dest);
    setMapCenter([dest.latitude, dest.longitude]);
    setSearchZoom(12);
    setTimeout(() => { setMapCenter(null); setSearchZoom(null); }, 2000);
  }, []);

  // ---- Data fetching ----
  useEffect(() => {
    fetchDestinations();
    fetchPOITypes();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Mobile search
  useEffect(() => {
    if (!mobileSearchQuery || mobileSearchQuery.length < 2) { setMobileSearchResults(null); setMobileSearchOpen(false); return; }
    const timer = setTimeout(async () => {
      setMobileSearchLoading(true);
      try {
        const response = await axios.get(`${API}/search`, { params: { q: mobileSearchQuery, limit: 10, include_geocode: true } });
        setMobileSearchResults(response.data);
        setMobileSearchOpen(true);
      } catch (error) { setMobileSearchResults(null); }
      finally { setMobileSearchLoading(false); }
    }, 300);
    return () => clearTimeout(timer);
  }, [mobileSearchQuery]);

  // ---- Computed values ----
  const states = [...new Set(destinations.map(d => d.state))].sort();
  const filteredDestinations = destinations.filter(dest => {
    if (selectedState !== "all" && dest.state !== selectedState) return false;
    return true;
  });

  useEffect(() => { fetchDesktopSearchResults(debouncedDesktopQuery); }, [debouncedDesktopQuery, fetchDesktopSearchResults]);

  const viewportDestinations = useMemo(() => {
    if (!viewportBounds) return filteredDestinations;
    return filteredDestinations.filter(dest => {
      if (!dest.latitude || !dest.longitude) return false;
      return (
        dest.latitude >= viewportBounds.south &&
        dest.latitude <= viewportBounds.north &&
        dest.longitude >= viewportBounds.west &&
        dest.longitude <= viewportBounds.east
      );
    });
  }, [filteredDestinations, viewportBounds]);

  const handleBoundsChange = useCallback((bounds) => {
    currentMapViewRef.current = { center: bounds.center, zoom: bounds.zoom };
    if (boundsTimeoutRef.current) clearTimeout(boundsTimeoutRef.current);
    boundsTimeoutRef.current = setTimeout(() => {
      setViewportBounds(prev => {
        if (!prev) return bounds;
        const t = 0.001;
        const changed = Math.abs(prev.north - bounds.north) > t || Math.abs(prev.south - bounds.south) > t || Math.abs(prev.east - bounds.east) > t || Math.abs(prev.west - bounds.west) > t;
        return changed ? bounds : prev;
      });
    }, 150);
  }, []);

  // Shared filter props
  const filterProps = {
    selectedState, setSelectedState, states,
    showRivers, setShowRivers,
    showLakes, setShowLakes,
    selectedPOITypes, setSelectedPOITypes,
    setShowPOIs, poiTypeConfig,
  };

  // Shared map props
  const mapProps = {
    destinations: filteredDestinations,
    selectedDestination,
    onDestinationSelect: handleDestinationSelect,
    onDestinationClick: handleDestinationNavigate,
    onToggleDestinationFavorite: saveInApp,
    onBoundsChange: handleBoundsChange,
    showStreamGauges: selectedPOITypes.includes('stream_gauge'),
    showRivers, showLakes, showPOIs, selectedPOITypes,
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="spinner" />
      </div>
    );
  }

  const flyToLocation = mapCenter ? {
    longitude: mapCenter[1],
    latitude: mapCenter[0],
    zoom: searchZoom || 12
  } : null;

  // ==================== MOBILE LAYOUT ====================
  if (isMobile) {
    return (
      <MobileDestinationsLayout
        mapProps={mapProps}
        flyToLocation={flyToLocation}
        filterProps={filterProps}
        mobileSearchQuery={mobileSearchQuery}
        setMobileSearchQuery={setMobileSearchQuery}
        mobileSearchResults={mobileSearchResults}
        setMobileSearchResults={setMobileSearchResults}
        mobileSearchLoading={mobileSearchLoading}
        mobileSearchOpen={mobileSearchOpen}
        setMobileSearchOpen={setMobileSearchOpen}
        mobileViewMode={mobileViewMode}
        setMobileViewMode={setMobileViewMode}
        viewportDestinations={viewportDestinations}
        selectedState={selectedState}
        setSelectedState={setSelectedState}
        states={states}
        toggleFavorite={saveInApp}
        onDestinationNavigate={handleDestinationNavigate}
        onSearchDestination={handleSearchDestination}
        onSearchAccessPoint={handleSearchAccessPoint}
        onSearchLocation={handleSearchLocation}
        directionsDialogOpen={directionsDialogOpen}
        setDirectionsDialogOpen={setDirectionsDialogOpen}
        directionsDestination={directionsDestination}
        directionsOrigin={directionsOrigin}
        setDirectionsOrigin={setDirectionsOrigin}
        getDirections={getDirections}
        navigate={navigate}
      />
    );
  }

  // ==================== DESKTOP LAYOUT ====================
  return (
    <div className="h-screen flex flex-col bg-background overflow-hidden" data-testid="destinations-page">
      {/* Header */}
      <header className="flex-shrink-0 z-50 bg-white border-b border-gray-200 shadow-sm">
        <div className="flex items-center gap-4 px-4 py-3">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 hover:opacity-80 transition-opacity" data-testid="back-btn">
            <Fish className="w-7 h-7 text-accent" />
            <span className="font-serif text-lg font-medium hidden md:block">DIY Fly Fishing Guide</span>
          </button>
          
          <div className="flex-1 mx-4 relative">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                ref={desktopSearchRef}
                type="text"
                placeholder="Search rivers, lakes, or locations..."
                value={desktopSearchQuery}
                onChange={(e) => setDesktopSearchQuery(e.target.value)}
                onKeyDown={handleDesktopKeyDown}
                onFocus={() => desktopSearchResults && setDesktopSearchOpen(true)}
                className="w-full h-12 pl-12 pr-12 text-base bg-gray-50 border border-gray-200 rounded-full focus:bg-white focus:border-accent focus-visible:ring-1 focus-visible:ring-accent"
                data-testid="desktop-search-input"
              />
              {desktopSearchLoading ? (
                <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 animate-spin" />
              ) : desktopSearchQuery && (
                <button onClick={clearDesktopSearch} className="absolute right-4 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-200 rounded-full transition-colors">
                  <X className="w-4 h-4 text-gray-500" />
                </button>
              )}
            </div>
            
            {desktopSearchOpen && desktopSearchResults && (
              <div ref={desktopDropdownRef} className="absolute top-full left-0 right-0 mt-2 z-[100]" data-testid="desktop-search-dropdown">
                <SearchResultsDropdown
                  searchResults={desktopSearchResults}
                  destinations={destinations}
                  selectedIndex={desktopSelectedIndex}
                  onSelectDestination={(dest) => handleDesktopSelectLocation({ latitude: dest.latitude, longitude: dest.longitude, type: "destination", data: dest })}
                  onSelectAccessPoint={(point) => handleDesktopSelectLocation({ latitude: point.latitude, longitude: point.longitude, type: "access_point", data: point })}
                  onSelectLocation={(loc) => handleDesktopSelectLocation({ latitude: loc.latitude, longitude: loc.longitude, type: "location", data: loc })}
                />
              </div>
            )}
          </div>
          
          <div className="flex items-center gap-3 ml-auto">
            <NavMenu />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex relative overflow-hidden">
        {/* Left Side Panel */}
        <div 
          className={`flex-shrink-0 bg-white border-r border-gray-200 flex flex-col transition-all duration-300 ${
            isPanelCollapsed ? 'w-0' : 'w-[400px]'
          }`}
          style={{ overflow: isPanelCollapsed ? 'hidden' : 'visible' }}
        >
          {!isPanelCollapsed && (
            <>
              <div className="flex-shrink-0 px-4 py-3 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="font-serif text-lg font-medium text-gray-900">Explore Destinations</h2>
                    <p className="text-sm text-gray-500">
                      {viewportDestinations.length} in view
                      {viewportDestinations.length !== filteredDestinations.length && (
                        <span className="text-gray-400"> &bull; {filteredDestinations.length} total</span>
                      )}
                    </p>
                  </div>
                  <button onClick={() => setIsPanelCollapsed(true)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" title="Collapse panel">
                    <PanelLeftClose className="w-5 h-5 text-gray-400" />
                  </button>
                </div>
              </div>
              
              <div className="flex-1 overflow-y-auto p-4">
                {viewportDestinations.length > 0 ? (
                  <div className="space-y-4">
                    {viewportDestinations.map((dest) => (
                      <DestinationCard
                        key={dest.id}
                        dest={dest}
                        isSelected={selectedDestination?.id === dest.id}
                        onToggleFavorite={saveInApp}
                        onSelect={() => handleDestinationSelect(dest)}
                        onNavigate={handleDestinationNavigate}
                        onDirections={openDirectionsDialog}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-center p-8">
                    <MapPin className="w-12 h-12 text-gray-300 mb-4" />
                    <h3 className="font-medium text-gray-700 mb-1">No destinations in view</h3>
                    <p className="text-sm text-gray-500">Zoom out or pan the map to see more destinations</p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
        
        {isPanelCollapsed && (
          <button
            onClick={() => setIsPanelCollapsed(false)}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-20 bg-white border border-gray-200 rounded-r-lg shadow-lg p-2 hover:bg-gray-50 transition-colors"
            title="Expand panel"
          >
            <PanelLeft className="w-5 h-5 text-gray-500" />
          </button>
        )}

        {/* Map */}
        <div className="flex-1 relative">
          <MapboxMap
            {...mapProps}
            flyToLocation={flyToLocation}
            className="w-full h-full"
          />
          <MapFilterBar {...filterProps} />
        </div>
      </div>

      <DestinationsDialogs
        directionsDialogOpen={directionsDialogOpen} setDirectionsDialogOpen={setDirectionsDialogOpen}
        directionsDestination={directionsDestination} directionsOrigin={directionsOrigin}
        setDirectionsOrigin={setDirectionsOrigin} getDirections={getDirections}
        weatherDialogOpen={weatherDialogOpen} setWeatherDialogOpen={setWeatherDialogOpen}
        selectedDestination={selectedDestination}
      />
    </div>
  );
}
